import * as React from "react"
import { Button } from "@/components/ui/button"

// Top-level error boundary wrapping the Inertia <App> in the client
// entrypoint. Without it, an uncaught render error tears down the React tree
// and leaves a dead, unscrollable shell — and in a standalone (Home Screen)
// app there's no browser chrome to reload from, so it's indistinguishable
// from a frozen tab. The boundary catches the error and offers a Reload
// button as the always-available escape hatch.
//
// Client-only: this is mounted from `entrypoints/inertia.ts`, never from the
// SSR entrypoint.
type Props = { children: React.ReactNode }
type State = { hasError: boolean }

export class RootErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: unknown, info: unknown) {
    // Surface to the console so it shows up in remote debugging / error
    // collectors. The UI stays minimal so it renders even if the design
    // system stylesheet failed to load.
    console.error("Uncaught render error:", error, info)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="flex min-h-screen items-center justify-center bg-page p-6">
        <div className="w-full max-w-md space-y-4 rounded-md border border-hairline bg-surface p-6 text-center">
          <h1>Something went wrong</h1>
          <p className="text-ink-body">
            The app hit an unexpected error. Reloading usually clears it.
          </p>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </div>
    )
  }
}
